/**
 * LeagueOfficialsPage — /league/:leagueId/officials
 * ─────────────────────────────────────────────────────────────────
 * Phase 4. Officials management surface.
 *
 * Two views:
 *   1. "Per-game" — list of upcoming games in the active season,
 *      each with its current officials slate. Click → modal to
 *      add/remove assignments for that game.
 *   2. "My assignments" — only shown when the current user has
 *      any assigned games (any role on any game). Quick way for
 *      a referee/scorekeeper to see their upcoming docket.
 *
 * Permissions (mirror backend):
 *   • Viewing the per-game list:  any league member.
 *   • Assigning / removing:       manage_officials.
 *   • Sign-off (this page):       performed BY the assigned user;
 *                                 no extra perm needed since it's
 *                                 their own assignment. Phase 5
 *                                 will wire the sign-flow itself
 *                                 (requires signature capture).
 *
 * Query string ?game=<gameId> auto-opens the assignment modal
 * for that game — used by the link from the Schedule page.
 *
 * 2026-09-28 — "Bulk assign": one crew onto many games (a date range,
 * a venue, a weekday, or hand-picked games) with a dry-run preview
 * (POST /seasons/:seasonId/officials/bulk). ?season=<id> deep links.
 */

import { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import {
  Shield, Calendar, MapPin, Clock, X, Plus, Trash2,
  UserPlus, CheckCircle2, ArrowRight,
  RefreshCw, Search, Users,
} from 'lucide-react';
import api from '../../services/api.js';
import toast from 'react-hot-toast';
import LeagueSubNav from './LeagueSubNav.jsx';
import { useLeagueMembership } from '../../hooks/useLeagueMembership.js';
// v2.5.125 — Phase 11 date consistency.
import { formatDate } from '../../utils/formatDate.js';

const ROLE_LABELS = {
  head_ref:    'Head referee',
  linesman:    'Linesman',
  timekeeper:  'Timekeeper',
  scorekeeper: 'Scorekeeper',
};
const ROLE_KEYS = ['head_ref', 'linesman', 'timekeeper', 'scorekeeper'];

export default function LeagueOfficialsPage() {
  const { leagueId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialGameId = searchParams.get('game');

  const { isManager, can, loading: roleLoading } = useLeagueMembership(leagueId);
  const canManage = isManager || can('manage_officials');

  const [league, setLeague]   = useState(null);
  const [seasons, setSeasons] = useState([]);
  const [seasonId, setSeasonIdState] = useState('');
  const [loading, setLoading] = useState(true);
  const setSeasonId = (id) => {
    setSeasonIdState(id);
    const next = new URLSearchParams(searchParams);
    if (id) next.set('season', id); else next.delete('season');
    setSearchParams(next, { replace: true });
  };

  const defaultSeasonId = useMemo(() => {
    if (seasons.length === 0) return '';
    const wanted = searchParams.get('season');
    if (wanted && seasons.some(s => s.id === wanted)) return wanted;
    const active = seasons.find(s => s.status === 'active');
    return active?.id || seasons[0].id;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seasons]);

  async function loadShell() {
    if (!leagueId) return;
    try {
      const [leagueRes, seasonsRes] = await Promise.all([
        api.get(`/leagues/${leagueId}`),
        api.get(`/leagues/${leagueId}/seasons`),
      ]);
      setLeague(leagueRes?.data?.league || null);
      setSeasons(seasonsRes?.data?.seasons || []);
    } catch (err) {
      if (err?.response?.status === 403) toast.error('You are not a member of this league');
      else if (err?.response?.status !== 401) toast.error('Could not load league');
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { loadShell(); /* eslint-disable-next-line */ }, [leagueId]);

  useEffect(() => {
    if (!seasonId && defaultSeasonId) setSeasonIdState(defaultSeasonId);
  }, [defaultSeasonId, seasonId]);

  if (loading || roleLoading) {
    return (
      <div className="animate-fade-in" style={{ maxWidth: 1080 }}>
        <div className="card" style={{ color: 'var(--text-2)', textAlign: 'center' }}>Loading…</div>
      </div>
    );
  }
  if (!league) {
    return (
      <div className="animate-fade-in" style={{ maxWidth: 1080 }}>
        <div className="card" style={{ color: 'var(--text-2)', textAlign: 'center' }}>
          League not found or no access.
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in" style={{ maxWidth: 1080 }}>
      <LeagueSubNav league={league} />

      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <div>
          <h2 className="section-title text-xl">Officials</h2>
          <p style={{ fontSize: 13, color: 'var(--text-2)', marginTop: 4 }}>
            Assign referees, linesmen, timekeepers, and scorekeepers to each game in the season.
          </p>
        </div>
      </div>

      {/* My assignments (if any) */}
      <MyAssignmentsCard />

      {seasons.length === 0 ? (
        <EmptyNoSeasonsCard leagueId={leagueId} />
      ) : (
        <>
          <div className="card" style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <Calendar size={16} color="var(--ice)" />
            <span style={{ fontSize: 13, fontWeight: 600 }}>Season:</span>
            <select value={seasonId} onChange={(e) => setSeasonId(e.target.value)}
              style={{ ...inputStyle, width: 'auto', minWidth: 240, flex: '1 1 280px' }}>
              {seasons.map(s => (
                <option key={s.id} value={s.id}>
                  {s.name} ({s.status})
                </option>
              ))}
            </select>
          </div>

          {seasonId && (
            <GamesOfficialsList
              leagueId={leagueId} seasonId={seasonId}
              canManage={canManage}
              autoOpenGameId={initialGameId}
            />
          )}
        </>
      )}
    </div>
  );
}

// ─── My assignments card (officials' personal view) ───────────

function MyAssignmentsCard() {
  const [assignments, setAssignments] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get('/officials/my-games?status=upcoming&limit=10')
      .then(({ data }) => { if (!cancelled) setAssignments(data?.assignments || []); })
      .catch(() => { /* silent — page also works for non-officials */ })
      .finally(() => { if (!cancelled) setLoaded(true); });
    return () => { cancelled = true; };
  }, []);

  if (!loaded || assignments.length === 0) return null;
  return (
    <div className="card" style={{ marginBottom: 16, borderColor: 'var(--goal-a40)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        <Shield size={14} color="var(--goal)" />
        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
          Your upcoming assignments ({assignments.length})
        </span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {assignments.map(a => (
          <div key={a.assignment_id} style={{
            display: 'flex', alignItems: 'center', gap: 12,
            padding: '8px 12px',
            background: 'var(--surface-2)', borderRadius: 8,
          }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600 }}>
                {a.home_team_name || '(home)'} vs {a.away_team_name || '(away)'}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-2)', marginTop: 2 }}>
                {formatDate(a.game_date)} {(a.game_time || '').slice(0, 5)}
                {' · '}{ROLE_LABELS[a.role] || a.role}
                {a.league_name && <> · {a.league_name}</>}
              </div>
            </div>
            <span className="badge" style={{
              background: a.signed_at ? 'var(--goal-a20)' : 'var(--ice-a10)',
              color: a.signed_at ? 'var(--goal)' : 'var(--ice)',
              fontSize: 10, padding: '2px 8px',
            }}>
              {a.signed_at ? 'Signed' : 'Assigned'}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function EmptyNoSeasonsCard({ leagueId }) {
  return (
    <div className="card" style={{ textAlign: 'center', padding: 36 }}>
      <Calendar size={36} color="var(--text-2)" style={{ margin: '0 auto 14px', opacity: 0.5 }} />
      <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>No seasons yet</div>
      <div style={{ fontSize: 13, color: 'var(--text-2)', marginBottom: 18 }}>
        Officials are assigned per game, and games belong to seasons. Create a season first.
      </div>
      <Link to={`/league/${leagueId}/seasons`} className="btn-ice"
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <Plus size={14} />
        Go to Seasons
        <ArrowRight size={12} />
      </Link>
    </div>
  );
}

// ─── Games list with officials slate ──────────────────────────

function GamesOfficialsList({ leagueId, seasonId, canManage, autoOpenGameId }) {
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openGameId, setOpenGameId] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get(`/leagues/${leagueId}/seasons/${seasonId}/games`);
      setGames(data?.games || []);
    } catch (err) {
      if (err?.response?.status !== 401) toast.error('Could not load games');
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [seasonId]);

  // Auto-open the requested game (from ?game=<id> in the URL) once
  // games load and we find it. Single-shot via a ref so we don't
  // keep re-opening if the user closes the modal.
  const autoOpenedRef = useRef(false);
  useEffect(() => {
    if (autoOpenGameId && !autoOpenedRef.current && !loading) {
      if (games.find(g => g.id === autoOpenGameId)) {
        setOpenGameId(autoOpenGameId);
        autoOpenedRef.current = true;
      }
    }
  }, [autoOpenGameId, games, loading]);

  if (loading) return <div className="card" style={{ color: 'var(--text-2)', textAlign: 'center' }}>Loading…</div>;
  if (games.length === 0) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: 36, color: 'var(--text-2)', fontSize: 13 }}>
        <Calendar size={36} style={{ margin: '0 auto 12px', opacity: 0.5 }} />
        No games in this season yet.
        <div style={{ marginTop: 10 }}>
          <Link to={`/league/${leagueId}/schedule`} style={{ color: 'var(--ice)' }}>
            Generate a schedule →
          </Link>
        </div>
      </div>
    );
  }

  // Filter out cancelled games — they don't need officials.
  const active = games.filter(g => g.status !== 'cancelled');
  // Group by month for visual structure.
  const grouped = (() => {
    const map = new Map();
    for (const g of active) {
      const ym = (g.game_date || '').slice(0, 7);
      if (!map.has(ym)) map.set(ym, []);
      map.get(ym).push(g);
    }
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  })();

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <span style={{ fontSize: 13, color: 'var(--text-2)' }}>
          {active.length} active game{active.length === 1 ? '' : 's'}
          {(() => {
            const open = active.filter(g => !g.is_scoresheet_locked && g.officials_assigned === 0).length;
            return open > 0 ? <span style={{ color: 'var(--puck)' }}> · {open} with no officials</span> : null;
          })()}
        </span>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button type="button" className="btn-ghost" onClick={load}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <RefreshCw size={12} />
            Refresh
          </button>
          {canManage && (
            <button type="button" className="btn-ice" onClick={() => setBulkOpen(true)}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Users size={13} />
              Bulk assign
            </button>
          )}
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {grouped.map(([ym, list]) => (
          <div key={ym}>
            <div style={{
              fontSize: 11, fontWeight: 700, color: 'var(--text-2)',
              textTransform: 'uppercase', letterSpacing: 0.6,
              marginBottom: 8,
            }}>
              {formatYearMonth(ym)}
            </div>
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              {list.map((g, i) => (
                <GameOfficialsRow key={g.id} game={g}
                  isLast={i === list.length - 1}
                  canManage={canManage}
                  onClick={() => setOpenGameId(g.id)} />
              ))}
            </div>
          </div>
        ))}
      </div>

      {bulkOpen && (
        <BulkAssignModal
          leagueId={leagueId}
          seasonId={seasonId}
          games={games}
          onClose={() => setBulkOpen(false)}
          onDone={() => { setBulkOpen(false); load(); }}
        />
      )}

      {openGameId && (
        <AssignmentsModal
          leagueId={leagueId}
          gameId={openGameId}
          game={games.find(g => g.id === openGameId)}
          canManage={canManage}
          onClose={() => setOpenGameId(null)}
          onChanged={load}
        />
      )}
    </>
  );
}

function GameOfficialsRow({ game, isLast, canManage, onClick }) {
  const totalSlots = 4;  // head_ref + linesman + timekeeper + scorekeeper
  const assignedRatio = `${game.officials_assigned}/${totalSlots}`;
  const fullyAssigned = game.officials_assigned >= totalSlots;
  const fullySigned   = game.officials_signed >= game.officials_assigned && game.officials_assigned > 0;

  return (
    <button type="button" onClick={onClick} style={{
      width: '100%', textAlign: 'left',
      display: 'flex', alignItems: 'center', gap: 14,
      padding: '12px 16px',
      borderBottom: isLast ? 'none' : '1px solid var(--border)',
      background: 'transparent', border: 'none', cursor: 'pointer',
      color: 'var(--text)',
    }}>
      <div style={{
        flexShrink: 0, width: 56, padding: '6px 4px',
        borderRadius: 8, background: 'var(--surface-2)',
        textAlign: 'center',
      }}>
        <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--text-2)', textTransform: 'uppercase' }}>
          {formatDow(game.game_date)}
        </div>
        <div style={{ fontSize: 16, fontWeight: 800, lineHeight: 1.1 }}>
          {parseInt((game.game_date || '').slice(8, 10), 10)}
        </div>
        <div style={{ fontSize: 9, color: 'var(--text-3)' }}>
          {formatMonShort(game.game_date)}
        </div>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 700 }}>
          {game.home_team_name || '(home)'} <span style={{ color: 'var(--text-3)' }}>vs</span> {game.away_team_name || '(away)'}
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-2)', marginTop: 2, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <Clock size={10} /> {(game.game_time || '').slice(0, 5)}
          </span>
          {game.venue_name && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <MapPin size={10} /> {game.venue_name}
            </span>
          )}
        </div>
      </div>
      <span className="badge" style={{
        background: fullySigned ? 'var(--goal-a20)'
          : fullyAssigned ? 'var(--ice-a10)'
          : 'var(--puck-a15)',
        color: fullySigned ? 'var(--goal)'
          : fullyAssigned ? 'var(--ice)'
          : 'var(--puck)',
        fontSize: 10, padding: '3px 8px', whiteSpace: 'nowrap',
        display: 'inline-flex', alignItems: 'center', gap: 4,
      }}>
        <Shield size={10} />
        {assignedRatio}
        {fullySigned && ' · signed'}
      </span>
      <ArrowRight size={14} color="var(--text-2)" />
    </button>
  );
}

// ─── Assignments modal ────────────────────────────────────────

function AssignmentsModal({ leagueId, gameId, game, canManage, onClose, onChanged }) {
  const [officials, setOfficials] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding]   = useState(null);  // role being added
  const [search, setSearch]   = useState('');
  const [saving, setSaving]   = useState(false);

  async function load() {
    setLoading(true);
    try {
      const [oRes, cRes] = await Promise.all([
        api.get(`/leagues/${leagueId}/games/${gameId}/officials`),
        api.get(`/leagues/${leagueId}/officials/candidates`).catch(() => ({ data: { candidates: [] } })),
      ]);
      setOfficials(oRes.data?.officials || []);
      setCandidates(cRes.data?.candidates || []);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not load officials');
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [gameId]);

  async function assign(role, userId) {
    setSaving(true);
    try {
      await api.post(`/leagues/${leagueId}/games/${gameId}/officials`,
        { role, assigned_user_id: userId || null });
      toast.success('Assigned');
      setAdding(null);
      setSearch('');
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not assign');
    } finally {
      setSaving(false);
    }
  }

  async function unassign(officialId, label) {
    if (!window.confirm(`Remove ${label}?`)) return;
    try {
      await api.delete(`/leagues/${leagueId}/games/${gameId}/officials/${officialId}`);
      toast.success('Removed');
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not remove');
    }
  }

  // Group officials by role for display.
  const byRole = useMemo(() => {
    const map = new Map();
    for (const k of ROLE_KEYS) map.set(k, []);
    for (const o of officials) {
      if (!map.has(o.role)) map.set(o.role, []);
      map.get(o.role).push(o);
    }
    return map;
  }, [officials]);

  // Filter candidates by search (name).
  const filteredCandidates = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return candidates;
    return candidates.filter(c => (c.display_name || '').toLowerCase().includes(q));
  }, [candidates, search]);

  return (
    <ModalBackdrop onClose={onClose}>
      {/* mobile-h (not 100vh) — on mobile Safari/Capacitor, 100vh doesn't
          shrink when the keyboard opens (e.g. the candidate search input
          below), so a plain '92vh' can clip the modal footer off-screen. */}
      <div className="card" style={{ maxWidth: 600, width: '100%', maxHeight: 'calc(var(--mobile-h, 100dvh) * 0.92)', overflowY: 'auto' }}>
        <ModalHeader title="Game officials" onClose={onClose} />

        {game && (
          <div style={{
            padding: 12, background: 'var(--surface-2)', borderRadius: 10,
            marginBottom: 14,
          }}>
            <div style={{ fontSize: 14, fontWeight: 700 }}>
              {game.home_team_name} vs {game.away_team_name}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 2 }}>
              {formatDate(game.game_date)} {(game.game_time || '').slice(0, 5)}
              {game.venue_name && <> · {game.venue_name}</>}
            </div>
          </div>
        )}

        {loading ? (
          <div style={{ color: 'var(--text-2)', textAlign: 'center', padding: 14 }}>Loading…</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {ROLE_KEYS.map(role => (
              <RoleSection
                key={role}
                role={role}
                assigned={byRole.get(role) || []}
                canManage={canManage}
                isAdding={adding === role}
                onAddClick={() => { setAdding(role); setSearch(''); }}
                onCancelAdd={() => { setAdding(null); setSearch(''); }}
                candidates={filteredCandidates}
                search={search}
                setSearch={setSearch}
                onAssign={(userId) => assign(role, userId)}
                onUnassign={unassign}
                saving={saving}
              />
            ))}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
          <button type="button" className="btn-ghost" onClick={onClose}>Close</button>
        </div>
      </div>
    </ModalBackdrop>
  );
}

function RoleSection({
  role, assigned, canManage, isAdding, onAddClick, onCancelAdd,
  candidates, search, setSearch, onAssign, onUnassign, saving,
}) {
  // For roles that conceptually allow multiple people (linesman),
  // we always allow "Add another". For singletons (head_ref) we
  // hide the add button once one is assigned. Backend's UNIQUE
  // constraint handles the "same user twice" case but doesn't
  // prevent multiple distinct users at the same role.
  const isSingleton = role !== 'linesman';
  const hideAddButton = isSingleton && assigned.length > 0;

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 10, padding: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Shield size={13} color="var(--ice)" />
          <span style={{ fontSize: 13, fontWeight: 700 }}>{ROLE_LABELS[role]}</span>
          {assigned.length > 0 && (
            <span style={{ fontSize: 11, color: 'var(--text-2)' }}>· {assigned.length}</span>
          )}
        </div>
        {canManage && !isAdding && !hideAddButton && (
          <button type="button" className="btn-ghost" style={tinyBtn} onClick={onAddClick}>
            <Plus size={11} style={{ marginRight: 4 }} />
            Add
          </button>
        )}
      </div>

      {assigned.length === 0 && !isAdding && (
        <div style={{ fontSize: 12, color: 'var(--text-3)', fontStyle: 'italic' }}>
          No one assigned yet.
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {assigned.map(o => (
          <div key={o.id} style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '8px 10px',
            background: 'var(--surface-2)', borderRadius: 8,
          }}>
            <div style={{
              width: 28, height: 28, borderRadius: '50%',
              background: o.signed_at ? 'var(--goal-a20)' : 'var(--surface)',
              border: `1px solid ${o.signed_at ? 'var(--goal)' : 'var(--border)'}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}>
              {o.signed_at
                ? <CheckCircle2 size={13} color="var(--goal)" />
                : <span style={{ fontSize: 10, color: 'var(--text-2)' }}>
                    {(o.assigned_user_name || o.official_name || '?').slice(0, 1).toUpperCase()}
                  </span>}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600 }}>
                {o.assigned_user_name || o.official_name || '(unnamed)'}
              </div>
              <div style={{ fontSize: 10, color: 'var(--text-2)' }}>
                {o.assigned_user_id ? 'Account-linked' : 'Ad-hoc'}
                {o.signed_at && <> · signed</>}
              </div>
            </div>
            {canManage && (
              <button type="button" className="btn-ghost"
                style={{ ...tinyBtn, color: 'var(--penalty)' }}
                disabled={!!o.signed_at}
                title={o.signed_at ? 'Cannot remove a signed assignment' : 'Remove'}
                onClick={() => onUnassign(o.id, o.assigned_user_name || o.official_name || ROLE_LABELS[role])}>
                <Trash2 size={11} />
              </button>
            )}
          </div>
        ))}
      </div>

      {isAdding && (
        <div style={{
          marginTop: 10, padding: 10,
          background: 'var(--surface)', border: '1px solid var(--ice-a25)', borderRadius: 8,
        }}>
          <div style={{ position: 'relative', marginBottom: 8 }}>
            <Search size={12} color="var(--text-2)" style={{
              position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
            }} />
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name…" autoFocus
              style={{ ...inputStyle, paddingLeft: 28 }} />
          </div>

          {candidates.length === 0 ? (
            <div style={{ fontSize: 12, color: 'var(--text-3)', padding: 8, textAlign: 'center' }}>
              No league members with referee/scorekeeper account roles.
              You can still assign someone ad-hoc — they'll sign in person.
            </div>
          ) : (
            <div style={{ maxHeight: 180, overflowY: 'auto', marginBottom: 8 }}>
              {candidates.map(c => (
                <button key={c.user_id} type="button"
                  onClick={() => onAssign(c.user_id)} disabled={saving}
                  style={{
                    width: '100%', textAlign: 'left',
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '6px 10px', borderRadius: 6,
                    background: 'transparent', border: 'none',
                    color: 'var(--text)', cursor: 'pointer',
                  }}>
                  <Avatar name={c.display_name} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12, fontWeight: 600 }}>{c.display_name}</div>
                    <div style={{ fontSize: 10, color: 'var(--text-2)', textTransform: 'capitalize' }}>
                      {c.account_role}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
            <button type="button" className="btn-ghost" style={tinyBtn}
              onClick={() => onAssign(null)} disabled={saving}
              title="Add an ad-hoc official who signs later">
              <UserPlus size={11} style={{ marginRight: 4 }} />
              Add ad-hoc
            </button>
            <button type="button" className="btn-ghost" style={tinyBtn} onClick={onCancelAdd}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Bulk assign (2026-09-28) ─────────────────────────────────
// One crew onto many games. Always previews first (dry run), then
// writes in one transaction. Locked and cancelled games are never
// touched and signed officials are never removed (server-enforced).
const DOW = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
function isoToday() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function isoPlusDays(n) {
  const d = new Date(); d.setDate(d.getDate() + n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function BulkAssignModal({ leagueId, seasonId, games, onClose, onDone }) {
  const [candidates, setCandidates] = useState(null);
  const [how, setHow] = useState('range');           // 'range' | 'pick'
  const [from, setFrom] = useState(isoToday());
  const [to, setTo] = useState(isoPlusDays(30));
  const [venueId, setVenueId] = useState('');
  const [weekday, setWeekday] = useState('');
  const [picked, setPicked] = useState(() => new Set());
  const [crew, setCrew] = useState([{ role: 'head_ref', user_id: '' }, { role: 'scorekeeper', user_id: '' }]);
  const [mode, setMode] = useState('fill_empty');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/leagues/${leagueId}/officials/candidates`)
      .then(({ data }) => setCandidates(data?.candidates || []))
      .catch(() => setCandidates([]));
  }, [leagueId]);

  const venues = useMemo(() => {
    const m = new Map();
    for (const g of games) if (g.venue_id && g.venue_name) m.set(g.venue_id, g.venue_name);
    return Array.from(m.entries()).map(([id, name]) => ({ id, name }));
  }, [games]);
  const pickable = useMemo(
    () => games.filter(g => g.status !== 'cancelled' && !g.is_scoresheet_locked),
    [games]);

  // Any edit invalidates the preview.
  useEffect(() => { setPreview(null); }, [how, from, to, venueId, weekday, picked, crew, mode]);

  const nameOf = (uid) => candidates?.find(c => c.user_id === uid)?.display_name || 'Someone';
  const body = (dryRun) => ({
    ...(how === 'pick'
      ? { game_ids: Array.from(picked) }
      : { from_date: from || undefined, to_date: to || undefined, venue_id: venueId || undefined,
          weekday: weekday === '' ? undefined : Number(weekday) }),
    assignments: crew.filter(c => c.user_id).map(c => ({ role: c.role, user_id: c.user_id })),
    mode,
    dry_run: dryRun,
  });
  const ready = crew.some(c => c.user_id) && (how === 'range' ? (from || to || venueId || weekday !== '') : picked.size > 0);

  async function run(dryRun) {
    setBusy(true);
    try {
      const { data } = await api.post(`/leagues/${leagueId}/seasons/${seasonId}/officials/bulk`, body(dryRun));
      if (dryRun) setPreview(data);
      else {
        toast.success(`Assigned ${data.added} official${data.added === 1 ? '' : 's'} across ${data.games_changed} game${data.games_changed === 1 ? '' : 's'}`
          + (data.removed ? ` (replaced ${data.removed})` : ''));
        onDone();
      }
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not assign officials');
    } finally {
      setBusy(false);
    }
  }

  const label = { display: 'block', fontSize: 11, color: 'var(--text-2)', marginBottom: 4, fontWeight: 600 };
  return (
    <ModalBackdrop onClose={busy ? () => {} : onClose}>
      <div className="card" style={{ maxWidth: 680, width: '100%', maxHeight: 'calc(var(--mobile-h, 100dvh) * 0.92)', overflowY: 'auto' }}>
        <ModalHeader title="Bulk assign officials" onClose={onClose} />

        <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>1 · Which games</div>
        <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
          {[['range', 'By date, venue or day'], ['pick', 'Pick games']].map(([id, text]) => (
            <button key={id} type="button" onClick={() => setHow(id)} className={how === id ? 'btn-ice' : 'btn-ghost'}
                    style={{ fontSize: 12, padding: '6px 12px' }}>{text}</button>
          ))}
        </div>
        {how === 'range' ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, marginBottom: 14 }}>
            <div><span style={label}>From</span><input type="date" value={from} onChange={(e) => setFrom(e.target.value)} style={inputStyle} /></div>
            <div><span style={label}>To</span><input type="date" value={to} onChange={(e) => setTo(e.target.value)} style={inputStyle} /></div>
            <div><span style={label}>Venue</span>
              <select value={venueId} onChange={(e) => setVenueId(e.target.value)} style={inputStyle}>
                <option value="">Any venue</option>
                {venues.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
              </select>
            </div>
            <div><span style={label}>Day</span>
              <select value={weekday} onChange={(e) => setWeekday(e.target.value)} style={inputStyle}>
                <option value="">Any day</option>
                {DOW.map((d, i) => <option key={d} value={i}>{d}s</option>)}
              </select>
            </div>
          </div>
        ) : (
          <div style={{ maxHeight: 220, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 10, marginBottom: 14 }}>
            {pickable.length === 0 && <div style={{ padding: 12, fontSize: 12, color: 'var(--text-2)' }}>No open games in this season.</div>}
            {pickable.map(g => (
              <label key={g.id} style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '8px 12px', borderBottom: '1px solid var(--border)', fontSize: 13, cursor: 'pointer' }}>
                <input type="checkbox" checked={picked.has(g.id)} onChange={(e) => {
                  const next = new Set(picked);
                  if (e.target.checked) next.add(g.id); else next.delete(g.id);
                  setPicked(next);
                }} />
                <span style={{ width: 92, color: 'var(--text-2)', fontSize: 12 }}>{formatDate(g.game_date)}</span>
                <span style={{ flex: 1 }}>{g.home_team_name} vs {g.away_team_name}</span>
                <span style={{ fontSize: 11, color: g.officials_assigned ? 'var(--text-2)' : 'var(--puck)' }}>{g.officials_assigned}/4</span>
              </label>
            ))}
          </div>
        )}

        <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>2 · The crew</div>
        {candidates && candidates.length === 0 && (
          <div style={{ fontSize: 12, color: 'var(--text-2)', marginBottom: 8 }}>
            No league members have a referee or scorekeeper account yet — add them under Members first.
          </div>
        )}
        {crew.map((c, i) => (
          <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <select value={c.role} onChange={(e) => setCrew(crew.map((x, j) => j === i ? { ...x, role: e.target.value } : x))}
                    style={{ ...inputStyle, flex: '0 1 170px', minWidth: 140 }}>
              {ROLE_KEYS.map(r => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
            </select>
            <select value={c.user_id} onChange={(e) => setCrew(crew.map((x, j) => j === i ? { ...x, user_id: e.target.value } : x))}
                    style={{ ...inputStyle, flex: '1 1 180px', width: 'auto', minWidth: 0 }}>
              <option value="">Choose a person…</option>
              {(candidates || []).map(p => <option key={p.user_id} value={p.user_id}>{p.display_name} ({p.account_role})</option>)}
            </select>
            <button type="button" className="btn-ghost" style={tinyBtn} aria-label="Remove row"
                    onClick={() => setCrew(crew.filter((_, j) => j !== i))} disabled={crew.length === 1}>
              <Trash2 size={11} />
            </button>
          </div>
        ))}
        {crew.length < 8 && (
          <button type="button" className="btn-ghost" style={{ ...tinyBtn, marginBottom: 14 }}
                  onClick={() => setCrew([...crew, { role: 'linesman', user_id: '' }])}>
            <Plus size={11} style={{ marginRight: 4 }} /> Add a role
          </button>
        )}

        <div style={{ fontSize: 12, fontWeight: 700, margin: '4px 0 6px' }}>3 · When a game already has someone in that role</div>
        <div style={{ display: 'grid', gap: 6, marginBottom: 14 }}>
          {[
            ['fill_empty', 'Leave it — only fill empty roles', 'Safest. Games that already have a head ref keep theirs.'],
            ['add', 'Add alongside', 'e.g. a second linesman.'],
            ['replace', 'Replace', 'Removes people in these roles who have not signed yet.'],
          ].map(([id, title, hint]) => (
            <label key={id} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13, cursor: 'pointer' }}>
              <input type="radio" name="bulk-mode" checked={mode === id} onChange={() => setMode(id)} style={{ marginTop: 3 }} />
              <span><strong>{title}</strong> <span style={{ color: 'var(--text-2)', fontSize: 12 }}>— {hint}</span></span>
            </label>
          ))}
        </div>

        {preview && (
          <div style={{ padding: 12, borderRadius: 10, background: 'var(--surface-2)', marginBottom: 14, fontSize: 13 }}>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>
              {preview.added > 0
                ? `Will assign ${preview.added} official${preview.added === 1 ? '' : 's'} across ${preview.games_changed} game${preview.games_changed === 1 ? '' : 's'}${preview.removed ? ` and replace ${preview.removed}` : ''}.`
                : 'Nothing to assign — every matching game already has these roles covered.'}
            </div>
            <div style={{ color: 'var(--text-2)', fontSize: 12 }}>
              {preview.games_matched} game{preview.games_matched === 1 ? '' : 's'} matched
              {preview.skipped_games?.length ? ` · ${preview.skipped_games.length} locked or cancelled (left alone)` : ''}
              {preview.skipped ? ` · ${preview.skipped} role${preview.skipped === 1 ? '' : 's'} already filled` : ''}
            </div>
            <div style={{ maxHeight: 160, overflowY: 'auto', marginTop: 8 }}>
              {(preview.games || []).filter(g => g.adds.length).map(g => (
                <div key={g.id} style={{ fontSize: 12, padding: '3px 0' }}>
                  {formatDate(g.game_date)} · {g.home_team_name} vs {g.away_team_name}:{' '}
                  <span style={{ color: 'var(--goal)' }}>{g.adds.map(a => `${ROLE_LABELS[a.role]} ${nameOf(a.user_id)}`).join(', ')}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
          <button type="button" className="btn-ghost" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="button" className="btn-ghost" onClick={() => run(true)} disabled={busy || !ready}>
            {busy && !preview ? 'Checking…' : 'Preview'}
          </button>
          <button type="button" className="btn-ice" onClick={() => run(false)}
                  disabled={busy || !ready || !preview || (preview.added === 0 && !preview.removed)}>
            {busy && preview ? 'Assigning…' : 'Assign'}
          </button>
        </div>
      </div>
    </ModalBackdrop>
  );
}

function Avatar({ name }) {
  const initials = (name || '?').split(/\s+/).slice(0, 2).map(s => s[0]?.toUpperCase() || '').join('') || '?';
  return (
    <div style={{
      width: 26, height: 26, borderRadius: '50%',
      background: 'var(--surface-2)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      fontSize: 11, fontWeight: 700, color: 'var(--text-2)',
    }}>
      {initials}
    </div>
  );
}

// ─── Shared bits ──────────────────────────────────────────────

function ModalBackdrop({ onClose, children }) {
  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0,
      background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 16, zIndex: 100,
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
        {children}
      </div>
    </div>
  );
}
function ModalHeader({ title, onClose }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
      <h2 className="section-title text-lg">{title}</h2>
      <button type="button" onClick={onClose}
        style={{ background: 'none', border: 'none', color: 'var(--text-2)', cursor: 'pointer' }}>
        <X size={18} />
      </button>
    </div>
  );
}

function formatYearMonth(ym) {
  if (!ym) return '';
  const [y, m] = ym.split('-');
  const date = new Date(parseInt(y, 10), parseInt(m, 10) - 1, 1);
  return date.toLocaleString(undefined, { month: 'long', year: 'numeric' });
}
function formatDow(dateStr) {
  if (!dateStr) return '';
  try { return new Date(dateStr + 'T00:00:00').toLocaleString(undefined, { weekday: 'short' }); }
  catch { return ''; }
}
function formatMonShort(dateStr) {
  if (!dateStr) return '';
  try { return new Date(dateStr + 'T00:00:00').toLocaleString(undefined, { month: 'short' }); }
  catch { return ''; }
}

const inputStyle = {
  width: '100%',
  background: 'var(--surface-2)',
  border: '1px solid var(--border)',
  borderRadius: 10,
  padding: '8px 12px',
  color: 'var(--text)',
  fontFamily: 'var(--font-body)',
  fontSize: 13,
  outline: 'none',
};
const tinyBtn = { padding: '4px 10px', fontSize: 11, minHeight: 'auto' };
